import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Typography, Alert, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import api from '../../utils/api';

const { Title, Text } = Typography;

const ResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [error, setError] = useState('');
  const { token } = useParams();
  const navigate = useNavigate();

  // Verify token on component mount
  useEffect(() => {
    const verifyToken = async () => {
      try {
        setLoading(true);
        await api.get(`/auth/verify-reset-token/${token}`);
        setTokenValid(true);
      } catch (err) {
        console.error('Token verification failed:', err);
        setTokenValid(false);
        setError('This password reset link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const onFinish = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('Passwords do not match!');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', {
        token,
        newPassword: values.newPassword
      });
      
      message.success('Password reset successful! Please login with your new password.');
      navigate('/login');
    } catch (err) {
      console.error('Password reset failed:', err);
      message.error(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (loading && tokenValid === null) {
    return <div>Verifying reset token...</div>;
  }

  if (tokenValid === false) {
    return (
      <div style={{ textAlign: 'center' }}>
        <Title level={4}>Invalid or Expired Link</Title>
        <Alert
          message="Error"
          description={error || 'This password reset link is invalid or has expired.'}
          type="error"
          showIcon
          style={{ margin: '16px 0' }}
        />
        <Button type="primary" onClick={() => navigate('/login')}>
          Back to Login
        </Button>
        <div style={{ marginTop: '16px' }}>
          <Text>Need a new reset link? </Text>
          <Button type="link" onClick={() => navigate('/forgot-password')} style={{ padding: 0 }}>
            Request another
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Title level={3} style={{ textAlign: 'center', marginBottom: '24px' }}>
        Reset Your Password
      </Title>
      
      <Form
        name="reset_password"
        onFinish={onFinish}
        layout="vertical"
      >
        <Form.Item
          name="newPassword"
          rules={[
            { required: true, message: 'Please input your new password!' },
            { min: 8, message: 'Password must be at least 8 characters long' }
          ]}
        >
          <Input.Password 
            prefix={<LockOutlined />} 
            placeholder="New Password" 
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Please confirm your password!' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('The two passwords do not match!'));
              },
            }),
          ]}
        >
          <Input.Password 
            prefix={<LockOutlined />} 
            placeholder="Confirm New Password" 
            size="large"
          />
        </Form.Item>

        <Form.Item>
          <Button 
            type="primary" 
            htmlType="submit" 
            size="large" 
            block
            loading={loading}
          >
            Reset Password
          </Button>
        </Form.Item>
      </Form>
      
      <div style={{ textAlign: 'center', marginTop: '16px' }}>
        <Text>Remember your password? </Text>
        <Button type="link" onClick={() => navigate('/login')} style={{ padding: 0 }}>
          Sign In
        </Button>
      </div>
    </div>
  );
};

export default ResetPassword;
